import { Injectable, inject, signal } from '@angular/core';
import { AppService } from '../../core/app.service';
import { BookingApiService } from '../../core/booking-api.service';
import { mapBackendOrderToRow } from './backend-order-mapper';
import { mapBackendDetailToBooking } from './detail-order-mapper';

/**
 * Powers the global top-bar search.
 *
 * <p>Resolves a free-text query — PNR, order reference (VB-…), ticket
 * number, passenger name, agent, route — against live orders and routes by
 * the rule the product owner asked for:
 * <ul>
 *   <li><b>Unique hit → open that booking's detail page directly.</b></li>
 *   <li>0 or many hits → land on All Bookings with the query pre-applied so
 *       the list shows the candidate set (or an empty state).</li>
 * </ul>
 *
 * <p>Search set = live orders from {@code GET /api/v1/orders} (the real
 * records worth deep-linking to). When the backend is unreachable we still
 * fall through to the list with the query seeded, so its own client-side
 * filter can cover whatever rows it has.
 */
@Injectable({ providedIn: 'root' })
export class GlobalBookingSearchService {
  private readonly api = inject(BookingApiService);
  private readonly app = inject(AppService);

  /** True while a query is being resolved — the top-bar shows a spinner and
   *  blocks re-submits. */
  readonly searching = signal(false);

  /** How many orders to pull for matching. The list view itself pages at 50;
   *  we cast a wider net so search reaches beyond the first page. */
  private static readonly SCAN_SIZE = 200;

  search(raw: string): void {
    const q = (raw ?? '').trim();
    if (!q || this.searching()) return;

    this.searching.set(true);
    this.api.listOrders({ page: 0, size: GlobalBookingSearchService.SCAN_SIZE }).subscribe({
      next: (page) => {
        const rows = (page?.content || []).map(mapBackendOrderToRow);
        const unique = this.pickUnique(rows, q);
        this.searching.set(false);
        if (unique) {
          this.openDetail(unique);
        } else {
          this.gotoList(q, this.substringMatches(rows, q).length);
        }
      },
      error: () => {
        // Backend down / unauthorised — hand the query to the list and let its
        // own filter do what it can over the rows it holds.
        this.searching.set(false);
        this.gotoList(q, null);
      },
    });
  }

  // ─── matching ────────────────────────────────────────────────────────────

  /** Space-joined, lower-cased searchable text for a row — identical field
   *  set to the All Bookings list filter so results stay consistent. */
  private haystack(b: any): string {
    return [
      b.ref, b.pnr, b.ticket, b.desc, b.agent,
      ...((b.pax || []).map((p: any) => p?.name)),
    ].filter(Boolean).join(' ').toLowerCase();
  }

  private substringMatches(rows: any[], q: string): any[] {
    const ql = q.toLowerCase();
    return rows.filter((b) => this.haystack(b).includes(ql));
  }

  /**
   * Returns the single unmistakable target, or null when the result is
   * ambiguous or empty.
   *
   * <p>Precedence: an <em>exact</em> match on a strong identifier
   * (reference / PNR / ticket number) wins — one exact hit is treated as
   * unique even if looser substring matches also exist. Only when there's no
   * single exact identifier do we fall back to "exactly one substring match".
   */
  private pickUnique(rows: any[], q: string): any | null {
    const ql = q.toLowerCase();
    const idEquals = (b: any) =>
      [b.ref, b.pnr, b.ticket].some((v) => v && String(v).toLowerCase() === ql);

    const exact = rows.filter(idEquals);
    if (exact.length === 1) return exact[0];
    if (exact.length > 1) return null; // ambiguous exact identifiers → list

    const sub = this.substringMatches(rows, q);
    return sub.length === 1 ? sub[0] : null;
  }

  // ─── routing ─────────────────────────────────────────────────────────────

  /** Open a booking's detail page — mirrors BookingsComponent.openDetail so a
   *  search hit and a list click land identically. */
  private openDetail(b: any): void {
    if (!b?.isLive || !b?.id) {
      this.app.selectedBooking.set(b);
      this.app.navigate('booking-detail');
      return;
    }
    this.app.toastKey('bookings.toasts.loadingBooking', undefined, 'info');
    this.api.getOrder(b.id).subscribe({
      next: (detail: any) => {
        const enriched = mapBackendDetailToBooking(detail);
        this.app.selectedBooking.set(enriched);
        this.app.navigate(enriched?.ref ? 'booking-detail/' + enriched.ref : 'booking-detail');
      },
      error: () => {
        // Fall back to the thin summary row so the user still lands somewhere.
        this.app.selectedBooking.set(b);
        this.app.navigate('booking-detail');
      },
    });
  }

  /** Seed the query on the All Bookings list and navigate there. `liveHits` is
   *  the live-order match count (null = backend was unreachable). */
  private gotoList(q: string, liveHits: number | null): void {
    this.app.bookingsSearch.set(q);
    this.app.navigate('bookings');
    if (liveHits === 0) {
      this.app.toast(`No bookings match “${q}”`, 'warn');
    }
  }
}
